import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { checklistService } from '../../apiServices/ChecklistService';
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import { useRoomId } from '../../hooks/useRoomId';
import { Flex } from '../Flex/Flex';
import { HideSideBar, ShowSideBar } from '../Icons';

const Container = styled.div<{ $isHidden: boolean }>`
  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box; /* Opera/IE 8+ */
  width: 352px;
  top: 49px;
  bottom: 0;
  padding: 24px;
  padding-top: 8px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  color: var(--text);
  font-family: sans-serif;
  text-align: left;
  z-index: 3;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  right: ${(props) => (props.$isHidden ? '-352px' : '0px')};
  transition: right 0.18s ease-in-out;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 0px;
`;

const Subtitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-muted);
`;

const Description = styled.p`
  font-size: 16px;
  color: var(--text-muted);
  margin-bottom: 16px;
  margin-top: 0;
`;

const ListContainer = styled.div`
  > div:nth-child(even) {
    background-color: rgba(21, 36, 55, 0.04);
  }
`;
const Checkbox = styled.input`
  margin-top: 2px;
  margin-right: 8px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  font-size: 16px;
  margin-bottom: 4px;
  padding: 0px;
  border-radius: 2px;

  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10+ and Edge */
  user-select: none; /* Standard syntax */
`;

const CheckboxLabel = styled.label`
  text-decoration: none;
  width: 100%;
  padding: 0px;
  &:hover {
    background-color: rgba(0, 122, 255, 0.1);
    cursor: pointer;
  }
  ${Checkbox}:checked + span {
    text-decoration: line-through;
    color: var(--border-dark);
  }
`;

const StyledLink = styled(RouterLink)`
  color: var(--active-blue);
  text-decoration: underline;
  cursor: pointer;
  margin-top: 4px;
  margin-left: 4px;
`;

const FloatingButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const FloatingButton = styled.button<{ $isVisible: boolean }>`
  position: fixed;
  top: 50px;
  right: 32px;
  z-index: 3;

  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem 0.5rem 0.5rem;
  gap: 4px;
  border-radius: 0px 0px 0px 2px;
  font-size: 14px;
  font-weight: 500;

  background: var(--surface);
  border: 1px solid var(--border-light);
  box-shadow:
    0 1px 0px rgba(21, 36, 55, 0.16),
    0 0 4px rgba(0, 0, 0, 0.08);
  inset: 0 2px 2px rgba(255, 255, 255, 0.8);

  cursor: pointer;

  &:hover {
    cursor: pointer;
    border: solid 1px var(--border);
    background: var(--surface-elevated);
  }

  right: ${(props) => (props.$isVisible ? '0px' : '-352px')};
  transition: right 0.18s ease-in-out;
`;

const HideButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 12px;
  border-radius: 2px;
  font-weight: 500;
  margin: 8px 0 0 0;
  padding: 0.4rem;
  gap: 4px;
  border: solid 1px var(--border-light);

  svg {
    fill: rgba(21, 36, 55, 1);
  }

  &:hover {
    background-color: var(--surface-elevated);
    border: solid 1px var(--border);
  }
`;

export const Checklist: React.FC = () => {
  const [isHidden, setIsHidden] = useState(false);

  const canvasId = useRoomId();
  const { checklists, fetchUser } = useUser();
  const checklist = checklists?.find((c) => c.canvasId === canvasId);
  const [items, setItems] = useState(checklist?.items || []);
  const toast = useToast();
  const intl = useIntl();

  const hideChecklist = () => {
    setIsHidden(true);
  };

  const showChecklist = () => {
    setIsHidden(false);
  };

  useEffect(() => {
    const shouldInitializeChecklist =
      items.length === 0 && checklist && checklist.items.length > 0;

    if (shouldInitializeChecklist) {
      setItems(checklist?.items || []);
    }
  }, [checklist, items.length]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, id } = e.target;

    if (!checklist) {
      return;
    }

    const newItems = items.map((item) => {
      if (item.label === id) {
        return { ...item, value: checked };
      }
      return item;
    });

    setItems(newItems);

    checklistService
      .editCanvas(canvasId, {
        items: newItems,
        checklistId: checklist.checklistId,
      })
      .then(() => {
        fetchUser();
      })
      .catch((e) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  if (!checklist) {
    return null;
  }

  return (
    <>
      <Container $isHidden={isHidden}>
        <FloatingButtonWrapper>
          <HideButton
            onClick={hideChecklist}
            title={intl.formatMessage({ id: 'checklist.hideButton' })}
          >
            <ShowSideBar />
            {intl.formatMessage({ id: 'checklist.hideButton' })}
          </HideButton>
        </FloatingButtonWrapper>
        <Flex $flexDirection="column" $alignItems="start">
          <Title>{checklist.title}</Title>
          <Subtitle>{checklist.subTitle}</Subtitle>
          <Description>{checklist.description}</Description>
        </Flex>
        <ListContainer>
          {items?.map((item, index) => (
            <ChecklistItem key={index}>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={item.value}
                  id={item.label}
                  onChange={onChange}
                />
                <span>{item.label}</span>
                {item.url ? (
                  <StyledLink to={item.url} target="_blank">
                    {intl.formatMessage({ id: 'checklist.readMore' })}
                  </StyledLink>
                ) : (
                  ''
                )}
              </CheckboxLabel>
            </ChecklistItem>
          ))}
        </ListContainer>
      </Container>

      <FloatingButton
        $isVisible={isHidden}
        onClick={showChecklist}
        title={intl.formatMessage({ id: 'checklist.showButton' })}
      >
        <HideSideBar />
        {intl.formatMessage({ id: 'checklist.showButton' })}
      </FloatingButton>
    </>
  );
};
